/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps, Ref, toChildArray } from 'preact';
import { forwardRef, useRef } from 'preact/compat';

import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useFormFieldContextProps } from '#hooks/UNSAFE_useFormFieldContextProps';
import { useId } from '#hooks/UNSAFE_useId';
import { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import { FlexboxProps } from '#utils/UNSAFE_interpolations/flexbox';
import { Size } from '#utils/UNSAFE_size';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { CheckboxRadioField } from '#PRIVATE_CheckboxRadioField';
import { RadioSetContext } from './RadioSetContext';
import { RadioItemContext } from '#UNSAFE_RadioItem/RadioItemContext';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useFocusWithin } from '#hooks/UNSAFE_useFocusWithin';
import { useRadioFocusManager } from '#hooks/PRIVATE_useRadioFocusManager';
import { TabbableModeContext, useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { Flex } from '#UNSAFE_Flex';
import { xUnits } from '#UNSAFE_Theme';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = TestIdProps & {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: string;
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * RadioItem
   */
  children: ComponentChildren;
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * layout direction of the children elements
   */
  direction?: FlexboxProps['direction'];
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the radio set requires a selection to be made.
   */
  isRequired?: boolean;
  /**
   * Specifies if the radio set is read-only.
   */
  isReadonly?: boolean;
  /**
   * Specifies if the radio set is disabled.
   */
  isDisabled?: boolean;
  /**
   * Label text for the group of radio items.
   *
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'none' | 'top' | 'start' | 'inside';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * Callback invoked each time the user changes selected option.
   */
  onCommit: (detail: ValueUpdateDetail<string | number | undefined>) => void;
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'compact'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * Value of the radio option.
   */
  value?: string | number;
};
/**
 * A radio set allows the user to select one option from a set of mutually exclusive options.
 */
export const RadioSet = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      columnSpan,
      direction = 'column',
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      onCommit,
      testId,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      children
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const hasValue = value != null;
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const formFieldContext = useFormFieldContextProps({
      hasValue,
      isDisabled,
      isReadonly
    });

    const baseId = useId();

    // When in readonly mode, we need to navigate the readonly divs using arrows keys to mimic default browser behavior for radiosets
    const containerRef = useRef<HTMLDivElement>(null);
    const { setFocusIndex, handleArrowKeyPress } = useRadioFocusManager(containerRef, children);

    const { focusProps, isFocused } = useFocusWithin({
      isDisabled
    });

    const { isTabbable } = useTabbableMode();

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TabbableModeContext.Provider
          value={{ isTabbable: isFocused && isReadonly ? false : isTabbable }}>
          <CheckboxRadioField
            aria-describedby={ariaDescribedBy}
            assistiveText={assistiveText}
            baseId={baseId}
            columnSpan={columnSpan}
            helpSourceLink={helpSourceLink}
            helpSourceText={helpSourceText}
            isDisabled={isDisabled}
            isReadonly={isReadonly}
            isRequired={isRequired}
            label={label}
            labelEdge={labelEdge}
            labelStartWidth={labelStartWidth}
            parentComponentVariant="radioSet"
            messages={messages}
            userAssistanceDensity={userAssistanceDensity}
            hasValue={hasValue}
            ref={ref}
            role="radiogroup"
            testId={testId}>
            <div
              ref={containerRef}
              onKeyDown={isReadonly ? handleArrowKeyPress : undefined}
              {...(isReadonly ? focusProps : {})}>
              <RadioSetContext.Provider
                value={{
                  name: baseId,
                  value,
                  onCommit,
                  setFocusIndex
                }}>
                <Flex
                  direction={direction}
                  {...(direction === 'row' && { gap: [0, xUnits(8) as Size], wrap: 'wrap' })}>
                  {toChildArray(children).map((child, index) => (
                    <RadioItemContext.Provider value={{ index }}>{child}</RadioItemContext.Provider>
                  ))}
                </Flex>
              </RadioSetContext.Provider>
            </div>
          </CheckboxRadioField>
        </TabbableModeContext.Provider>
      </FormFieldContext.Provider>
    );
  }
);
